/**
 * AI FIRSATLARI — iki bölüm:
 *   1. KAÇAN SORGULAR (Mention Gap): AI'ın rakibi anıp SENİ anmadığı sorgular
 *      (Google AI Overview + ChatGPT/Gemini/Claude/Perplexity) → tek tık brief köprüsü.
 *   2. AI SENİ BÖYLE ANLATIYOR: anıldığın cevaplarda ton (olumlu/nötr/temkinli) + temalar.
 */
import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Sparkles, Loader2, PenLine, MessageSquareQuote, ChevronDown, ChevronUp, Trophy } from "lucide-react";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009", purple: "#7a5af8" };

type Veri = {
  gap: { soru: string; motor: string; hacim: number | null; rakipler: string[] }[];
  gapSayisi: number;
  sentiment: { olumlu: number; notr: number; temkinli: number; temalar: string[] } | null;
  tonlar: { soru: string; motor: string; ton: string; temalar: string[] }[];
  aioVar: boolean; visVar: boolean;
};

const TON_STIL: Record<string, { renk: string; ad: string }> = {
  olumlu: { renk: C.green, ad: "olumlu" }, notr: { renk: C.amber, ad: "nötr" }, temkinli: { renk: C.red, ad: "temkinli" },
};

export default function AiFirsatlar() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { data: d, isLoading } = useQuery<Veri>({
    queryKey: ["ai-firsatlar", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/ai-firsatlar`);
      if (!r.ok) throw new Error("ai-firsatlar");
      return r.json();
    },
  });

  type SerpFirsat = { keyword: string; volume: number; position: number | null; url: string | null; kacanlar: string[]; sahipOlunan: string[] };
  const { data: serp } = useQuery<{ firsatlar: SerpFirsat[]; toplam: number }>({
    queryKey: ["serp-firsatlar", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/serp-firsatlar`)).json(),
  });

  const [, navigate] = useLocation();
  const [kazanBekleyen, setKazanBekleyen] = useState<string | null>(null);
  // AKILLI KÖPRÜ: içerik varsa ona, brief varsa ona, yoksa TEK brief üretip açar.
  const sorguyuKazan = async (sorgu: string) => {
    if (kazanBekleyen) return;
    setKazanBekleyen(sorgu);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/sorguyu-kazan`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ sorgu }) });
      const j = (await r.json()) as { durum?: string; id?: number; pageId?: number; mesaj?: string; kanibalUyari?: string | null; error?: string };
      if (!r.ok) { alert(j.error ?? "İşlem başarısız"); return; }
      if (j.kanibalUyari) alert(j.kanibalUyari);
      switch (j.durum) {
        // Sorguyu hedefleyen sayfa zaten varsa yeni içerik üretmek kanibalizasyon —
        // doğru hamle o sayfayı güçlendirmek; kullanıcıya söyleyip oraya götür.
        case "sayfa-var": alert(j.mesaj ?? "Bu sorgu için sayfan zaten var — güçlendirme açılıyor."); navigate(`/page-strengthen/${j.pageId}`); break;
        case "icerik-var": navigate(`/content/${j.id}`); break;
        case "havuzda": alert(j.mesaj ?? "Brief yazar havuzunda bekliyor."); navigate("/content-opportunities"); break;
        case "brief-var":
        case "uretildi": navigate(`/planner-brief/${encodeURIComponent(sorgu)}`); break;
        case "planda-var": alert(j.mesaj ?? "Takvimde zaten planlı."); break;
        case "kosu-yok": alert(j.mesaj ?? "Önce Brief Keşif Ajanı çalışmalı."); navigate("/content-planner"); break;
        default: alert("Beklenmedik yanıt");
      }
    } catch { alert("Bağlantı hatası"); }
    finally { setKazanBekleyen(null); }
  };
  const KazanButonu = ({ sorgu }: { sorgu: string }) => (
    <Button size="sm" variant="outline" disabled={kazanBekleyen != null} className="h-7 gap-1 text-[11px]" onClick={() => void sorguyuKazan(sorgu)} title="İçerik/brief varsa açar; yoksa bu sorgu için brief üretir (~1 token)">
      {kazanBekleyen === sorgu ? <Loader2 className="h-3 w-3 animate-spin" /> : <PenLine className="h-3 w-3" />} {kazanBekleyen === sorgu ? "Hazırlanıyor…" : "Bu sorguyu kazan"}
    </Button>
  );

  const [gapAzalan, setGapAzalan] = useState(true);
  const siraliGap = useMemo(() => {
    const g = [...(d?.gap ?? [])];
    g.sort((a, b) => (gapAzalan ? (b.hacim ?? -1) - (a.hacim ?? -1) : (a.hacim ?? Number.MAX_SAFE_INTEGER) - (b.hacim ?? Number.MAX_SAFE_INTEGER)));
    return g;
  }, [d?.gap, gapAzalan]);

  const s = d?.sentiment;
  const tonToplam = s ? s.olumlu + s.notr + s.temkinli : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${C.purple}15` }}><Sparkles className="h-6 w-6" style={{ color: C.purple }} /></div>
        <div>
          <h1 className="text-xl font-bold">AI Fırsatları</h1>
          <p className="text-sm text-muted-foreground">Yapay zekâ cevaplarında rakibin anıldığı ama senin olmadığın sorgular — ve anıldığında AI'ın seni nasıl anlattığı.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />AI verileri toplanıyor…</div>
      ) : !d || (!d.aioVar && !d.visVar) ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Henüz AI görünürlük verisi yok — önce bir AI Görünürlük / AI Overview koşusu gerekiyor (Sıralama Takibi'nden başlatılır).</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* Anlatı tonu */}
          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><MessageSquareQuote className="h-4 w-4" style={{ color: C.purple }} /> AI seni böyle anlatıyor</div>
            {!s || tonToplam === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">Ton analizi bir sonraki AI Görünürlük koşusunda otomatik dolacak (anıldığın cevaplar sınıflandırılır).{d.tonlar.length === 0 && d.visVar ? " Son koşu bu özellikten önce yapılmış." : ""}</p>
            ) : (
              <>
                <div className="mb-2 flex h-3 overflow-hidden rounded-full" title={`olumlu ${s.olumlu} · nötr ${s.notr} · temkinli ${s.temkinli}`}>
                  {s.olumlu > 0 && <div style={{ width: `${(s.olumlu / tonToplam) * 100}%`, background: C.green }} />}
                  {s.notr > 0 && <div style={{ width: `${(s.notr / tonToplam) * 100}%`, background: C.amber }} />}
                  {s.temkinli > 0 && <div style={{ width: `${(s.temkinli / tonToplam) * 100}%`, background: C.red }} />}
                </div>
                <div className="mb-2 flex gap-3 text-[12px]">
                  <span style={{ color: C.green }}>● olumlu {s.olumlu}</span><span style={{ color: C.amber }}>● nötr {s.notr}</span><span style={{ color: C.red }}>● temkinli {s.temkinli}</span>
                </div>
                {s.temalar.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
                    <span className="text-muted-foreground">AI'ın seninle andığı temalar:</span>
                    {s.temalar.map((t) => <span key={t} className="rounded-full border border-gray-200 px-2 py-0.5 dark:border-gray-700">{t}</span>)}
                  </div>
                )}
                {d.tonlar.filter((t) => t.ton === "temkinli").slice(0, 3).map((t) => (
                  <div key={t.soru + t.motor} className="mt-2 rounded border-l-2 py-1 pl-2 text-[12px] text-muted-foreground" style={{ borderColor: C.red }}>
                    <b style={{ color: C.red }}>{t.motor}</b> "{t.soru}" cevabında temkinli anlatıyor{t.temalar.length ? ` (${t.temalar.join(", ")})` : ""}.
                  </div>
                ))}
              </>
            )}
          </CardContent></Card>

          {/* Kaçan sorgular */}
          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">Kaçan Sorgular <span className="text-[11px] font-normal text-muted-foreground">— rakip anılıyor, sen yoksun ({d.gapSayisi})</span></div>
            <p className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground dark:bg-white/[0.03]">Bu liste şunu söylüyor: insanlar bu soruları <b>yapay zekâya soruyor</b> (aylık sayısı "Aylık AI arama" sütununda), yapay zekâ cevabında <b>rakiplerini kaynak gösteriyor</b> ama seni göstermiyor. "Bu sorguyu kazan" o soruya senin cevabını üretme sürecini başlatır: içerik varsa açar, yoksa brief üretir.</p>
            {d.gap.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">Kaçan sorgu görünmüyor — takip edilen sorgularda ya anılıyorsun ya da rakip de anılmıyor. 👏</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
                  <thead><tr className="border-b-2 border-gray-100 text-left dark:border-gray-800">
                    <th className="px-2 py-2 font-semibold">Sorgu</th><th className="px-2 py-2">Motor</th><th className="cursor-pointer select-none px-2 py-2 text-center" onClick={() => setGapAzalan((v) => !v)} title="Bu soru yapay zekâ aramalarında ayda tahminen kaç kez soruluyor — tıklayınca sıralar">Aylık AI arama {gapAzalan ? <ChevronDown className="inline h-3 w-3" /> : <ChevronUp className="inline h-3 w-3" />}</th><th className="px-2 py-2">Anılan rakipler</th><th className="px-2 py-2" />
                  </tr></thead>
                  <tbody>
                    {siraliGap.map((g, i) => (
                      <tr key={i} className="border-b border-gray-50 align-top dark:border-gray-800/60">
                        <td className="max-w-[300px] px-2 py-2">{g.soru}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-[11.5px] text-muted-foreground">{g.motor}</td>
                        <td className="px-2 py-2 text-center tabular-nums" title={g.hacim != null ? `ayda ~${g.hacim.toLocaleString("tr-TR")} kez soruluyor` : "yalnız motor koşusundan geldi — hacim ölçülmüyor"}>{g.hacim != null ? g.hacim.toLocaleString("tr-TR") : "—"}</td>
                        <td className="max-w-[260px] px-2 py-2 text-[11.5px] text-muted-foreground">{g.rakipler.join(", ")}</td>
                        <td className="px-2 py-2">
                          <KazanButonu sorgu={g.soru} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">Kaynak: Google AI Overview soruları + dört motorun görünürlük koşusu. "Bu sorguyu kazan" → Brief Keşif'te o sorgu için içerik brief'i hazırlar.</p>
          </CardContent></Card>

          {/* SERP özellik fırsatları */}
          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Trophy className="h-4 w-4" style={{ color: C.amber }} /> SERP Özellik Fırsatları <span className="text-[11px] font-normal text-muted-foreground">— ilk 10'dasın ama vitrini başkası kapmış ({serp?.toplam ?? 0})</span></div>
            {!serp || serp.firsatlar.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">Fırsat görünmüyor — ya takipteki kelimelerde özellik yok ya da kapmışsın. Veri gece sıralama takibinden gelir.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                  <thead><tr className="border-b-2 border-gray-100 text-left dark:border-gray-800">
                    <th className="px-2 py-2 font-semibold">Kelime</th><th className="px-2 py-2 text-center">Hacim</th><th className="px-2 py-2 text-center">Sıra</th><th className="px-2 py-2">Kaçan vitrin</th><th className="px-2 py-2" />
                  </tr></thead>
                  <tbody>
                    {serp.firsatlar.map((f) => (
                      <tr key={f.keyword} className="border-b border-gray-50 align-top dark:border-gray-800/60">
                        <td className="max-w-[240px] truncate px-2 py-2" title={f.url ?? undefined}>{f.keyword}</td>
                        <td className="px-2 py-2 text-center tabular-nums">{f.volume || "—"}</td>
                        <td className="px-2 py-2 text-center tabular-nums font-medium">#{f.position}</td>
                        <td className="px-2 py-2"><div className="flex flex-wrap gap-1">{f.kacanlar.map((k) => <span key={k} className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: `${C.amber}18`, color: C.amber }}>{k}</span>)}{f.sahipOlunan.map((k) => <span key={k} className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: `${C.green}15`, color: C.green }}>✓ {k}</span>)}</div></td>
                        <td className="px-2 py-2">
                          <KazanButonu sorgu={f.keyword} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">İlk 10'da sıralandığın kelimede snippet/PAA/video gibi vitrin başkasındaysa, doğru yapılandırılmış içerik güncellemesi o vitrini alabilir — sıra yükseltmekten çok daha ucuz trafik.</p>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}
